// ─── CyberHealth Scanner — Shared TypeScript Types ────────────────────────────

export type ScanStatus = 'idle' | 'scanning' | 'success' | 'error';

export type Severity  = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type RiskLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface Finding {
  category:           string;
  severity:           Severity;
  // Format original (DNS/SSL/Ports)
  title?:             string;
  technical_detail?:  string;
  plain_explanation?: string;
  penalty?:           number;
  // Format extra checks (En-têtes HTTP / Email / Tech / Réputation)
  message?:           string;
  // Commun aux deux
  recommendation:     string;
  // Premium finding (détails masqués pour les plans gratuits)
  is_premium?:        boolean;
  // Accepted risk (risque accepté par l'utilisateur)
  accepted_risk?:     boolean;
  accepted_risk_justification?: string;
  accepted_risk_date?: string;
  accepted_risk_by?:  string;
}

export interface PortDetail {
  service:  string;
  open:     boolean;
  severity: string;
}

export interface SubdomainInfo {
  subdomain:  string;
  ip:         string | null;
  active:     boolean;
}

export interface CertInfo {
  subdomain:   string;
  expires_at:  string | null;
  days_left:   number;
  expired:     boolean;
  expiring_soon: boolean;
  error?:      string;
}

export interface SubdomainDetails {
  subdomains:    SubdomainInfo[];
  expired_certs: CertInfo[];
  expiring_soon: CertInfo[];
  orphaned:      string[];
  total_found:   number;
}

export interface VulnDetails {
  server_header:   string;
  powered_by:      string;
  detected_stack:  Array<{ tech: string; version: string }>;
}

export interface ScanResult {
  scan_id:           string;
  domain:            string;
  scanned_at:        string;
  security_score:    number;
  risk_level:        RiskLevel;
  findings:          Finding[];
  dns_details:       Record<string, unknown>;
  ssl_details:       Record<string, unknown>;
  port_details:      Record<string, PortDetail>;
  recommendations:   string[];
  scan_duration_ms:  number;
  meta:              Record<string, unknown>;
  // Champs premium
  subdomain_details: SubdomainDetails | Record<string, never>;
  vuln_details:      VulnDetails      | Record<string, never>;
  breach_details?:   BreachDetails;
  typosquat_details?: TyposquatDetails;
  ct_details?:        CtDetails;
  // Conformité réglementaire — tous plans
  compliance?:       ComplianceData;
  // Partage viral : jeton HMAC permettant à un visiteur anonyme d'activer le
  // lien public /r/{scan_id} de son scan (absent pour les comptes connectés,
  // qui utilisent le toggle authentifié PATCH /scans/history/{uuid}/share).
  share_token?:      string | null;
}

export interface ComplianceArticle {
  code:           string;
  framework:      'NIS2' | 'RGPD';
  title:          string;
  title_en:       string;
  description:    string;
  description_en: string;
  compliant:      boolean | null;
  status:         'pass' | 'warn' | 'fail' | 'not_assessable';
  triggered_by:   string[];
}

export interface ComplianceCriterion {
  id:           string;
  label_fr:     string;
  label_en:     string;
  regulations:  string[];
  article_fr:   string;
  article_en:   string;
  desc_fr:      string;
  desc_en:      string;
  status:       'pass' | 'warn' | 'fail';
}

export interface ComplianceData {
  nis2_score:     number;
  rgpd_score:     number;
  overall_level:  'bon' | 'insuffisant' | 'critique';
  nis2:           ComplianceArticle[];
  rgpd:           ComplianceArticle[];
  criteria:       ComplianceCriterion[];
  disclaimer_fr:  string;
  disclaimer_en:  string;
}

export interface BreachDetails {
  status:        'clean' | 'breached' | 'no_api_key' | 'error';
  breach_count?: number;
  breach_names?: string[];
}

export interface TyposquatHit {
  domain:       string;
  variant_type: 'tld' | 'missing' | 'double' | 'transposition' | 'homoglyph' | 'keyboard';
  ip:           string;
}

export interface TyposquatDetails {
  status:    'clean' | 'squatted';
  checked:   number;
  hit_count: number;
  hits:      TyposquatHit[];
}

export interface CtCertRecord {
  common_name: string;
  name_value:  string;
  issuer:      string;
  logged_at:   string;
  not_before:  string;
  not_after:   string;
}

export interface CtDetails {
  status:         'no_data' | 'certs_found';
  total_found:    number;
  recent_7days:   number;
  recent_30days:  number;
  wildcard_count: number;
  issuers:        string[];
  recent_certs:   CtCertRecord[];
  wildcard_certs: CtCertRecord[];
}

export interface ConsoleLog {
  id:        string;
  message:   string;
  type:      'info' | 'success' | 'warning' | 'error' | 'system';
  timestamp: string;
}

// Couleurs Tailwind par sévérité
export const SEVERITY_CONFIG: Record<Severity, {
  border: string; bg: string; text: string; badge: string; icon: string;
}> = {
  CRITICAL: {
    border: 'border-red-200',
    bg:     'bg-red-50',
    text:   'text-red-600',
    badge:  'bg-red-50 text-red-600 border border-red-200',
    icon:   'text-red-500',
  },
  HIGH: {
    border: 'border-orange-200',
    bg:     'bg-orange-50',
    text:   'text-orange-600',
    badge:  'bg-orange-50 text-orange-600 border border-orange-200',
    icon:   'text-orange-500',
  },
  MEDIUM: {
    border: 'border-amber-200',
    bg:     'bg-amber-50',
    text:   'text-amber-600',
    badge:  'bg-amber-50 text-amber-700 border border-amber-200',
    icon:   'text-amber-500',
  },
  LOW: {
    border: 'border-emerald-200',
    bg:     'bg-emerald-50',
    text:   'text-emerald-600',
    badge:  'bg-emerald-50 text-emerald-600 border border-emerald-200',
    icon:   'text-emerald-500',
  },
  INFO: {
    border: 'border-blue-200',
    bg:     'bg-blue-50',
    text:   'text-blue-600',
    badge:  'bg-blue-50 text-blue-600 border border-blue-200',
    icon:   'text-blue-500',
  },
};

// Score → couleur de la jauge
export function scoreColor(score: number): {
  gauge: string; glow: string; label: string; textClass: string;
} {
  if (score >= 70) return {
    gauge:     '#16a34a',
    glow:      '0 1px 10px rgba(22,163,74,0.25)',
    label:     'Bon',
    textClass: 'text-emerald-600',
  };
  if (score >= 40) return {
    gauge:     '#ea580c',
    glow:      '0 1px 10px rgba(234,88,12,0.22)',
    label:     'Risqué',
    textClass: 'text-orange-600',
  };
  return {
    gauge:     '#dc2626',
    glow:      '0 1px 10px rgba(220,38,38,0.22)',
    label:     'Critique',
    textClass: 'text-red-600',
  };
}
